/// <amd-module name="typeahead/core/EssDataProvider" />
/// <amd-dependency path="jquery" name="$" />
/// <amd-dependency path="underscore" name="_" />

/// <reference path="../../typings/typeahead/ExpediaSuggestServiceRequestGenerator.d.ts" />

"use strict";
import {IDataProvider, IDataProviderResponse} from "typeahead/core/IDataProvider";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";

export interface IEssResponse extends IDataProviderResponse {
    activeABTests: string;
    query: string;
    responseCode: string;
    isReferralToGoogleMaps: boolean;
}

export enum GoogleRequestType {GOOGLE_AUTOCOMPLETE, ADDRESS_AUTOCOMPLETE}

export class EssDataProvider implements IDataProvider {

    private static isReferralToGoogleMaps(responseCode: string): boolean {
        return ([GoogleRequestType[GoogleRequestType.ADDRESS_AUTOCOMPLETE],
            GoogleRequestType[GoogleRequestType.GOOGLE_AUTOCOMPLETE]].indexOf(responseCode) > -1);
    }

    /**
     * Function that given a search query string, a flag for ignoring results (used as a seed call)
     * and a set of options, returns a Promise that when it resolves contains an object with two properties.
     *  1) a results list containing all the items returned from the call
     *  2) the original search query used to make the HTTP request(s)
     * @param srchText - search query string
     * @param ignoreResults - fire the request and ignore the response
     * @param options - object hash containing the list of options.
     * @param $ - reference to jQuery instance to use
     * NOTE: the required properties for the options hash are:
     *  {
     *    additionalDataSourceUrl: "",
     *    version: "v2",
     *    overrideServiceUrl: "",
     *    serviceUrl: "//suggest.expedia.com",
     *    mask: defaultMask,
     *    locale: "en_US",
     *    abtest: "",
     *    lob: LOB_ATTRIBUTE.FLIGHTS,
     *    dest: false,
     *    maxitems: 20,
     *    clientId: "",
     *    siteId: "",
     *    guid: "",
     *    features: ""
     *  }
     * @returns an instance of a Promise
     */
    public fetchData(options: any): JQueryPromise<IEssResponse> {

        let optionalServicePromise: JQueryPromise<any>;
        let requestGenerator: IRequestGenerator = <IRequestGenerator> options.requestGenerator;
        let isDualRequest: boolean = options.additionalDataSourceUrl ? true : false;
        let context: IContext = <IContext> options.context;
        let essServicePromise: JQueryPromise<any> = requestGenerator.makeRequest(options.srchText, options, context);

        if (options.overrideDataRequest && typeof options.overrideDataRequest === "function") {
            return options.overrideDataRequest(options.srchText)
                .then(this.singleSourceResponseHandler);
        }
        if (options.ignoreResults) {
            return essServicePromise.then($.noop);
        }
        if (isDualRequest) {
            optionalServicePromise = requestGenerator.makeRequest(options.srchText,
                $.extend(options, {isSecondary: true}), context);
            // jQuery $.when() is akin to Promise.all() fetching results from two Ajax calls here
            return $.when(optionalServicePromise, essServicePromise)
                .then(this.dualSourceResponseHandler, this.dualSourceErrorResponseHandler);
        }
        return essServicePromise.then(this.singleSourceResponseHandler);
    }

    private dualSourceResponseHandler(jsonResponseData1: any, jsonResponseData2: any): IEssResponse {
        // jsonResponseData1 and jsonResponseData2 are arguments resolved for two ajax requests, respectively.
        // Each argument is an array with the following structure: [ data, statusText, jqXHR ]
        // so jsonResponseData1[0] and jsonResponseData2[0] are the data from each Promise/Deferred
        let combinedResults = [];
        let query = jsonResponseData1[0].q; // both searches use the same srchTerm

        if (jsonResponseData1[0] && jsonResponseData1[0].sr) {
            combinedResults = combinedResults.concat(jsonResponseData1[0].sr);
        }

        if (jsonResponseData2[0] && jsonResponseData2[0].sr) {
            combinedResults = combinedResults.concat(jsonResponseData2[0].sr);
        } else if (jsonResponseData2[0] && jsonResponseData2[0].r) {
            combinedResults = combinedResults.concat(jsonResponseData2[0].r);
        }

        let combinedResponseCode = jsonResponseData1[0].rc + "|" + jsonResponseData2[0].rc;
        let activeABTests: string = _.compact([jsonResponseData1[0].activeABTests,
            jsonResponseData2[0].activeABTests]).join("|");

        return {
            isReferralToGoogleMaps: EssDataProvider.isReferralToGoogleMaps(combinedResponseCode),
            query,
            responseCode: combinedResponseCode, activeABTests,
            results: (combinedResults || []),
        };
    }

    private dualSourceErrorResponseHandler(): void {
        TypeaheadErrorLogger.log(new Error("Dual HTTP request failure handler. Called with " + arguments.length +
            " inputs"), Severity.Excusable,  ProblemOrigin.ExpediaSuggestService);
    }

    private singleSourceResponseHandler(jsonResponseData): IEssResponse {
        let results = jsonResponseData.sr ? jsonResponseData.sr : jsonResponseData.r;
        let query = jsonResponseData.q;

        return {
            activeABTests: jsonResponseData.activeABTests,
            isReferralToGoogleMaps: EssDataProvider.isReferralToGoogleMaps(jsonResponseData.rc),
            query,
            responseCode: jsonResponseData.rc,
            results: (results || []),
        };
    }
}

/**
 * Function to allow the caller to provide the version of jQuery they want to use
 * and not rely on the page load time AMD module loader to provide one. This facilitates
 * unit testing so you can mock/stub/fake the jQuery functionality used in here.
 * @param jQuery
 */
export function overrideJQuery(jQuery: JQueryStatic): void {
    $ = jQuery;
}
